import { Popover as PopoverPrimitive } from 'radix-ui'
import * as React from 'react'

import { cn } from '../../utils/cn'
import { NestedDialogContext, useNestedDialogRegistry } from './nestedDialogLayering'

function Popover({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

function PopoverTrigger({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

function PopoverContent({
  className,
  align = 'center',
  sideOffset = 4,
  children,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  const { hasNestedDialog, registry } = useNestedDialogRegistry()
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        className={cn(
          // Fade only — no zoom or slide. shadcn's default pairs the fade with
          // zoom-in-95 and slide-in-from-*, which reads as the panel scaling and
          // drifting into place; on anything larger than a menu that looks off.
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 w-72 origin-(--radix-popover-content-transform-origin) rounded-md border p-4 shadow-md outline-hidden',
          'z-200 transition-opacity',
          // A dialog opened from inside this popover is the newer surface, so get out of
          // its way until it closes. Hidden rather than lowered: Radix's Popper wrapper
          // copies the content's z-index onto itself once, at mount, and that wrapper is
          // what actually stacks — so changing this element's z-index later does nothing.
          hasNestedDialog && 'pointer-events-none opacity-0',
          className
        )}
        {...props}
      >
        <NestedDialogContext.Provider value={registry}>{children}</NestedDialogContext.Provider>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
}

function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />
}

function PopoverClose({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Close>) {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />
}

function PopoverHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="popover-header" className={cn('flex flex-col gap-1 text-sm', className)} {...props} />
  )
}

function PopoverTitle({ className, ...props }: React.ComponentProps<'h2'>) {
  return <div data-slot="popover-title" className={cn('font-medium', className)} {...props} />
}

function PopoverDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return <p data-slot="popover-description" className={cn('text-muted-foreground', className)} {...props} />
}

export {
  Popover,
  PopoverAnchor,
  PopoverClose,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
}
